import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { isAxiosError } from 'axios';
import dayjs from 'dayjs';
import {
  CalendarIcon,
  CircleXIcon,
  MailsIcon,
  PackageIcon,
  RouteIcon,
} from 'lucide-react';

import CarrierVerificationCard from 'components/CarrierVerification/CarrierVerificationCard';
import ErrorBoundary from 'components/ErrorBoundary';
import KitEmailSuggestions from 'components/Kit/EmailSuggestions/KitEmailSuggestions';
import { KitEmptyStateType } from 'components/Kit/EmailSuggestions/components/MessageBubble';
import { LOAD_VIEW_PIPELINES } from 'components/Kit/EmailSuggestions/constants';
import LoadContextProvider from 'components/LoadContextProvider';
import StarredLoadList from 'components/StarredLoadList';
import StarredLoadToggle from 'components/StarredLoadToggle';
import TMSProvider from 'components/TMSProvider';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TabsTriggerVariants,
} from 'components/Tabs';
import { Toaster } from 'components/ToasterProvider';
import { Flex } from 'components/layout';
import SidebarLoader from 'components/loading/SidebarLoader';
import { Typography } from 'components/typography';
import { AvailableTabs } from 'constants/SidebarTabs';
import { isQuickQuoteUiEnabled } from 'contexts/serviceContext';
import {
  SidebarStateContext,
  isEmailPlatform,
} from 'contexts/sidebarStateContext';
import { TMSContextType } from 'contexts/tms';
import useFetchLoadByFreightTrackingID from 'hooks/useFetchLoadByFreightTrackingID';
import { fetchStarredLoads } from 'hooks/useFetchStarredLoads';
import useFetchSuggestions from 'hooks/useFetchSuggestions';
import { fetchViewedLoads } from 'hooks/useFetchViewedLoads';
import { useQuoteViewSuggestionsPrefetch } from 'hooks/useQuoteViewSuggestionsPrefetch';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import InfoCircleIcon from 'icons/InfoCircle';
import putViewedLoadHistory from 'lib/api/putViewedLoadHistory';
import { newTMSHost } from 'lib/hosts/tms/interface';
import AppointmentSchedulingSection from 'pages/LoadView/AppointmentTab';
import LoadInformationTab from 'pages/LoadView/LoadInformationTab';
import OrderTab from 'pages/LoadView/OrderTab';
import OrdersTab from 'pages/LoadView/OrdersTab';
import OutboxTab from 'pages/LoadView/OutboxTab';
import CarrierCheckInSection from 'pages/LoadView/TrackAndTrace/CarrierCheckIn';
import CheckCallSection from 'pages/LoadView/TrackAndTrace/CheckCalls';
import ShipperCheckInSection from 'pages/LoadView/TrackAndTrace/ShipperCheckIn';
import UnifiedGlobalTranzForm from 'pages/QuoteView/LoadBuilding/GlobalTranzSectionForms/UnifiedGlobalTranzForm';
import UnifiedPlsLoadForm from 'pages/QuoteView/LoadBuilding/PlsSectionForms/UnifiedPlsLoadForm';
import UnifiedRevenovaLoadForm from 'pages/QuoteView/LoadBuilding/RevenovaSectionForms/UnifiedRevenovaLoadForm';
import UnifiedThreeGLoadForm from 'pages/QuoteView/LoadBuilding/ThreeGSectionForms/UnifiedThreeGLoadForm';
import UnifiedTransportProLoadForm from 'pages/QuoteView/LoadBuilding/TransportProSectionForms/UnifiedTransportProLoadForm';
import TurvoLoadForm from 'pages/QuoteView/LoadBuilding/TurvoLoadForm';
import { Email } from 'types/Email';
import { ExternalLinks } from 'types/ExternalLinks';
import { Load, normalizeLoad } from 'types/Load';
import { LoadAttributes } from 'types/LoadAttributes';
import { PendingOutboxEmails } from 'types/PendingOutboxEmail';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { Warehouse } from 'types/Warehouse';
import CheckCallSource from 'types/enums/CheckCallSource';
import { TMS } from 'types/enums/Integrations';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import {
  CheckCallSuggestion,
  SuggestedLoadChange,
} from 'types/suggestions/LoadSuggestions';
import captureException from 'utils/captureException';
import { isKitSuggestionActionable } from 'utils/completeKitSuggestion';
import { cn } from 'utils/shadcn';

import {
  TabItem,
  getTabKey,
  useLoadSearch,
} from '../../contexts/loadSearchContext';
import useFetchOrderByTrackingId from '../../hooks/useFetchOrderByTrackingId';
import CloneRecap from './Cloning/CloneRecap';
import LoadCloneTaskCard from './Cloning/TaskCard';
import OverviewSection from './OverviewSection';
import SaveAttachmentsToThreeG from './SaveAttachmentsToThreeG';
import ExceptionSection from './TrackAndTrace/Exceptions';
import NoteSection from './TrackAndTrace/Notes';
import { useLoadCloneJob } from './useLoadCloneJob';

interface LoadsSidebarProps {
  email: Maybe<Email>;
}

export default function LoadsSidebar({ email }: LoadsSidebarProps) {
  const { addTab, tabs, setTabs, activeTabKey, setActiveTabKey } =
    useLoadSearch();

  const {
    currentState: { starredLoads },
    setCurrentState,
  } = useContext(SidebarStateContext);

  const {
    serviceFeaturesEnabled,
    tmsIntegrations,
    carrierVerificationIntegrations,
  } = useServiceFeatures();
  const {
    isOrderEnabled,
    isCarrierVerificationEnabled,
    isLoadViewEnabled,
    isQuoteViewEnabled,
    isAppointmentSchedulingEnabled,
    isTrackAndTraceEnabled,
    isCarrierNetworkQuotingEnabled,
    isLoadBuildingEnabled,
    isTruckListEnabled,
  } = serviceFeaturesEnabled;

  // Prefetch Quote-view pulse while Loads is mounted
  useQuoteViewSuggestionsPrefetch({
    email,
    enabled: isQuoteViewEnabled,
    serviceFeaturesEnabled,
  });

  // Check if any major features are enabled
  const hasAnyFeaturesEnabled =
    isLoadViewEnabled ||
    isQuoteViewEnabled ||
    isAppointmentSchedulingEnabled ||
    isTrackAndTraceEnabled ||
    isQuickQuoteUiEnabled(serviceFeaturesEnabled) ||
    isCarrierNetworkQuotingEnabled ||
    isLoadBuildingEnabled ||
    isTruckListEnabled ||
    isCarrierVerificationEnabled;

  // Check if only carrier verification is enabled (no TMS integration)
  const isOnlyCarrierVerificationEnabled =
    isCarrierVerificationEnabled &&
    (!tmsIntegrations || tmsIntegrations.length === 0);

  // Get the first enabled carrier verification integration
  const enabledCarrierVerificationIntegration =
    carrierVerificationIntegrations?.[0] ?? null;

  // Load View always hydrates starred and viewed loads for search and navigation.
  useEffect(() => {
    fetchStarredLoads(setCurrentState);
    fetchViewedLoads(setCurrentState);
  }, [setCurrentState]);

  const closeTab = useCallback(
    (tabToClose: TabItem) => {
      setTabs((prevTabs) => {
        const tabToCloseIndex = prevTabs.findIndex(
          (tab) => getTabKey(tab) === getTabKey(tabToClose)
        );

        if (tabToCloseIndex === -1) {
          return prevTabs;
        }

        const newTabs = prevTabs.filter((_, i) => i !== tabToCloseIndex);

        if (getTabKey(tabToClose) !== activeTabKey) {
          return newTabs;
        }

        let newActiveTab: Undef<TabItem>;
        if (newTabs.length > 0) {
          const newIndex = Math.min(tabToCloseIndex, newTabs.length - 1);
          newActiveTab = newTabs[newIndex];
        }

        setActiveTabKey(newActiveTab ? getTabKey(newActiveTab) : '');
        return newTabs;
      });
    },
    [activeTabKey, setActiveTabKey, setTabs]
  );

  const activeTab = tabs.find((tab) => getTabKey(tab) === activeTabKey);

  return (
    <div className='flex-1 w-full'>
      <Flex direction='col'>
        <Flex direction='col' className='w-full flex-1 shrink-0'>
          <Tabs
            value={activeTabKey}
            onValueChange={setActiveTabKey}
            className='w-full flex-1 shrink-0 flex flex-col mt-2'
          >
            <TabsList className='w-full gap-[12px] relative flex items-center justify-between pr-4'>
              <Flex gap='md' align='center' className='flex-1 overflow-x-auto'>
                {tabs?.length > 0 &&
                  tabs.map((tab) => (
                    <>
                      <TabsTrigger
                        value={getTabKey(tab)}
                        key={getTabKey(tab)}
                        className={cn(
                          'text-sm rounded-none! inline-block w-auto shrink text-ellipsis overflow-hidden',
                          activeTabKey === getTabKey(tab) && 'shrink-0'
                        )}
                      >
                        <Flex align='center' gap='xs'>
                          {isOrderEnabled && tab.type === 'order' && (
                            <PackageIcon className='h-3 w-3' />
                          )}
                          {tab.id}
                        </Flex>
                      </TabsTrigger>
                      {activeTabKey === getTabKey(tab) && (
                        <CircleXIcon
                          className='h-4 w-4 shrink-0 -ml-1 stroke-brand-main cursor-pointer'
                          onClick={() => closeTab(tab)}
                        />
                      )}
                    </>
                  ))}
              </Flex>

              <Flex align='center' gap='md' className='ml-4 shrink-0 py-2'>
                {activeTab && (
                  <StarredLoadToggle
                    tab={activeTab.id}
                    starredLoads={starredLoads}
                  />
                )}
                <StarredLoadList
                  tab={activeTab?.id ?? ''}
                  starredLoads={starredLoads}
                  addLoadTab={(id) => addTab(id, 'load')}
                />
              </Flex>
            </TabsList>

            <div className='flex-1 shrink-0'>
              {(() => {
                if (!hasAnyFeaturesEnabled) {
                  return (
                    <Flex
                      direction='col'
                      align='center'
                      justify='center'
                      className='flex-1 p-8'
                    >
                      <div className='text-center mb-6 max-w-sm'>
                        <Typography
                          variant='h4'
                          weight='semibold'
                          className='mb-3'
                          align='center'
                        >
                          Welcome to Drumkit!
                        </Typography>
                        <Typography
                          variant='body-sm'
                          textColor='muted'
                          className='mb-4'
                          align='center'
                        >
                          Your Drumkit features are not yet configured. Contact
                          your Drumkit representative to get started with load
                          management, quoting, and more.
                        </Typography>
                        <Typography
                          variant='body-xs'
                          textColor='muted'
                          align='center'
                        >
                          Need help? Reach out to your account manager or
                          support team.
                        </Typography>
                      </div>
                    </Flex>
                  );
                }

                if (tabs.length === 0) {
                  return (
                    <Flex
                      direction='col'
                      align={
                        isOnlyCarrierVerificationEnabled && email?.from
                          ? 'stretch'
                          : 'center'
                      }
                      justify={
                        isOnlyCarrierVerificationEnabled && email?.from
                          ? 'start'
                          : 'center'
                      }
                      className={
                        isOnlyCarrierVerificationEnabled && email?.from
                          ? 'flex-1 w-full max-w-full min-h-[min(85vh,640px)] px-2 py-4 sm:px-3'
                          : 'flex-1 w-full p-0 pt-4'
                      }
                    >
                      <div className='w-full shrink-0'>
                        <KitEmailSuggestions
                          suggestions={[]}
                          threadFreightTrackingIDs={email?.freightTrackingIDs}
                          loadEmptyStateType={
                            (email?.freightTrackingIDs?.filter((id) =>
                              id.trim()
                            ).length ?? 0) > 0
                              ? KitEmptyStateType.AssociatedLoadsClosed
                              : KitEmptyStateType.NoAssociatedLoads
                          }
                          onOpenAssociatedLoad={(id) => addTab(id, 'load')}
                          onOpenAllAssociatedLoads={() => {
                            (email?.freightTrackingIDs ?? [])
                              .filter((id) => id.trim())
                              .forEach((id) => addTab(id, 'load'));
                          }}
                        />
                      </div>
                      {isOnlyCarrierVerificationEnabled && email?.from ? (
                        <CarrierVerificationCard
                          fromEmail={email.from}
                          enabledIntegration={
                            enabledCarrierVerificationIntegration
                          }
                          defaultExpanded={true}
                          hideCollapseToggle
                        />
                      ) : (
                        email?.from &&
                        isCarrierVerificationEnabled && (
                          <CarrierVerificationCard
                            fromEmail={email.from}
                            enabledIntegration={
                              enabledCarrierVerificationIntegration
                            }
                          />
                        )
                      )}
                    </Flex>
                  );
                }

                return tabs.map((tab) => (
                  <TabsContent
                    value={getTabKey(tab)}
                    key={getTabKey(tab)}
                    className='pt-4'
                  >
                    {(() => {
                      if (tab.type === 'load') {
                        return (
                          <LoadsSidebarLoadTab
                            email={email}
                            freightTrackingID={tab.id}
                            closeTab={() => closeTab(tab)}
                          />
                        );
                      }
                      if (isOrderEnabled) {
                        return (
                          <LoadsSidebarOrderTab
                            orderId={tab.id}
                            closeTab={() => closeTab(tab)}
                          />
                        );
                      }
                      return (
                        <div className='px-4 text-error-main'>
                          Order functionality is not enabled.
                        </div>
                      );
                    })()}
                  </TabsContent>
                ));
              })()}
            </div>
          </Tabs>
        </Flex>
      </Flex>
      <Toaster />
    </div>
  );
}

type LoadsSidebarOrderTabProps = {
  orderId: string;
  closeTab: () => void;
};

function LoadsSidebarOrderTab({
  orderId,
  closeTab,
}: LoadsSidebarOrderTabProps) {
  const { order, isLoading, error } = useFetchOrderByTrackingId(orderId);
  const { setTabs, setActiveTabKey } = useLoadSearch();

  const orderNotFound =
    !isLoading &&
    error &&
    isAxiosError(error) &&
    error.response?.status === 404;

  // If order not found, close the tab
  useEffect(() => {
    if (orderNotFound) {
      closeTab();
    }
  }, [orderNotFound, closeTab]);

  // If order has a load associated with it, redirect to show the load
  useEffect(() => {
    if (order && order.loadId && order.externalLoadId) {
      setTabs((prevTabs) => {
        const associatedLoadTab: TabItem = {
          id: order.externalLoadId!,
          type: 'load',
        };

        const loadTabExists = prevTabs.some(
          (tab) =>
            tab.id === associatedLoadTab.id &&
            tab.type === associatedLoadTab.type
        );

        if (loadTabExists) {
          // The load tab already exists, so just close this order tab
          // and switch to the load tab.
          setActiveTabKey(getTabKey(associatedLoadTab));
          return prevTabs.filter(
            (tab) => !(tab.id === orderId && tab.type === 'order')
          );
        } else {
          // The load tab doesn't exist, so replace this order tab with a new load tab.
          const newTabs = prevTabs.map((tab) =>
            tab.id === orderId && tab.type === 'order' ? associatedLoadTab : tab
          );
          setActiveTabKey(getTabKey(associatedLoadTab));
          return newTabs;
        }
      });
    }
  }, [order, orderId, setTabs, setActiveTabKey]);

  // If order has a load with externalLoadId, don't render the order view
  if (order && order.loadId && order.externalLoadId) {
    return (
      <div className='flex-1 shrink-0'>
        <SidebarLoader />
        <div className='px-4 text-neutral-600'>
          Redirecting to associated load...
        </div>
      </div>
    );
  }

  // If order not found, render nothing while tab is closing
  if (orderNotFound) {
    return null;
  }

  return (
    <div className='flex-1 shrink-0'>
      {!order && isLoading && <SidebarLoader />}

      {order && !isLoading && !order.loadId && (
        <ErrorBoundary>
          <OrderTab order={order} />
        </ErrorBoundary>
      )}

      {orderNotFound && (
        <div className='px-4 text-error-main'>
          Order not found for #{orderId}.
        </div>
      )}

      {!orderNotFound && orderId && !order && !isLoading && (
        <div className='px-4 text-error-main'>Error fetching order.</div>
      )}
    </div>
  );
}

type LoadsSidebarLoadTabProps = {
  email: MaybeUndef<Email>;
  freightTrackingID: string;
  closeTab: () => void;
};

function LoadsSidebarLoadTab({
  email,
  freightTrackingID,
  closeTab,
}: LoadsSidebarLoadTabProps) {
  const {
    tmsID,
    tmsName,
    load,
    loadAttributes,
    externalLinks,
    invalidate,
    invalidateKey,
    isLoading,
    pendingOutboxEmails,
    pickupWarehouse: loadPickupWarehouse,
    dropoffWarehouse: loadDropoffWarehouse,
    error: fetchError,
  } = useFetchLoadByFreightTrackingID(freightTrackingID);
  const loadNotFoundToastShownForRef = useRef<Maybe<string>>(null);
  // Tracks load IDs already submitted to viewed history for this mounted tab.
  // Failed submissions are removed so they can be retried. A ref keeps the set
  // across re-renders without triggering re-renders.
  const viewedLoadIDsRef = useRef<Set<string>>(new Set());

  const { toast } = useToast();

  const { tmsIntegrations } = useServiceFeatures();

  // A service may have multiple TMSes, so we need to get the specific one for this load
  const tmsTenant =
    tmsIntegrations?.find((tms) => tms.id === tmsID)?.tenant ?? '';

  const loadNotFound =
    !isLoading &&
    fetchError &&
    isAxiosError(fetchError) &&
    fetchError.response?.status === 404;

  // If load not found, notify the user and close the tab.
  useEffect(() => {
    if (loadNotFound) {
      if (loadNotFoundToastShownForRef.current !== freightTrackingID) {
        loadNotFoundToastShownForRef.current = freightTrackingID;
        toast({
          description: `Load ${freightTrackingID} not found.`,
          variant: 'destructive',
        });
      }
      closeTab();
    }
  }, [loadNotFound, closeTab, freightTrackingID, toast]);

  useEffect(() => {
    if (
      isLoading ||
      loadNotFound ||
      !load ||
      load.isPlaceholder ||
      !freightTrackingID ||
      viewedLoadIDsRef.current.has(freightTrackingID)
    ) {
      return;
    }

    viewedLoadIDsRef.current.add(freightTrackingID);

    const updateViewedLoads = async () => {
      const res = await putViewedLoadHistory(freightTrackingID);
      if (!res.isOk()) {
        // Remove Load ID from list of viewed ones since submitting view event failed
        viewedLoadIDsRef.current.delete(freightTrackingID);
        console.error('putViewedLoadHistory response not OK:', res);
      }
    };

    updateViewedLoads().catch((error) => {
      // Remove Load ID from list of viewed ones since submitting view event failed
      viewedLoadIDsRef.current.delete(freightTrackingID);
      console.error('Error updating viewed loads:', error);
    });
  }, [freightTrackingID, isLoading, load, loadNotFound]);

  const invalidateLoad = async (
    load?: Maybe<Load>,
    attrs?: Maybe<LoadAttributes>
  ) => {
    if (load) {
      await invalidate({
        tmsID,
        tmsName,
        externalLinks,
        load,
        loadAttributes: attrs ?? loadAttributes,
        pendingOutboxEmails,
        pickupWarehouse: loadPickupWarehouse,
        dropoffWarehouse: loadDropoffWarehouse,
      });
      return;
    }
    invalidate();
  };

  // If load not found, render nothing while tab is closing
  if (loadNotFound) {
    return null;
  }

  return (
    <div className='flex-1 shrink-0'>
      {!load && isLoading && <SidebarLoader />}

      {/* {!isLoading && load && load.moreThanTwoStops && (
        <div className='px-4 text-error-main'>
          {'We will soon support multi-stop loads!'}
        </div>
      )} */}

      {load && !isLoading && !load.isPlaceholder && (
        <LoadTabContent
          tmsID={tmsID}
          tmsName={tmsName}
          tenant={tmsTenant}
          email={email}
          load={load!}
          loadAttributes={loadAttributes!}
          externalLinks={externalLinks}
          loadPickupWarehouse={loadPickupWarehouse!}
          loadDropoffWarehouse={loadDropoffWarehouse!}
          invalidate={invalidateLoad}
          invalidateKey={invalidateKey}
          pendingOutboxEmails={pendingOutboxEmails!}
        />
      )}

      {loadNotFound && (
        <div className='px-4 text-error-main'>
          Load not found for #{freightTrackingID}.
        </div>
      )}

      {/* We live lookup a load from TMS when a user opens email/searches for it. So if it's still a placeholder at this point,
  something went wrong with the live lookup (429, network timeout, etc) */}
      {(!loadNotFound && freightTrackingID && !load && !isLoading) ||
        (load?.isPlaceholder && (
          <div className='px-4 text-error-main'>Error fetching load.</div>
        ))}
    </div>
  );
}

type LoadTabContentProps = Omit<TMSContextType, 'isTestingEnvironment'> & {
  email: MaybeUndef<Email>;
  load: Load;
  loadAttributes: LoadAttributes;
  externalLinks: MaybeUndef<ExternalLinks>;
  loadPickupWarehouse: MaybeUndef<Warehouse>;
  loadDropoffWarehouse: MaybeUndef<Warehouse>;
  invalidate: (load?: Load, loadAttributes?: LoadAttributes) => Promise<void>;
  invalidateKey: number;
  pendingOutboxEmails: MaybeUndef<PendingOutboxEmails>;
};

export function LoadTabContent({
  tmsID,
  tmsName,
  email,
  load,
  loadAttributes,
  externalLinks,
  loadPickupWarehouse,
  loadDropoffWarehouse,
  invalidate,
  invalidateKey,
  pendingOutboxEmails,
}: LoadTabContentProps) {
  const { addTab } = useLoadSearch();
  const {
    serviceFeaturesEnabled: {
      isAppointmentSchedulingEnabled,
      isTrackAndTraceEnabled,
      isExceptionsEnabled,
      isCheckCallCarrierSOPEnabled,
      isCheckCallShipperSOPEnabled,
      isCheckCallNotesEnabled,
      isCarrierEmailOutboxEnabled,
      isOrderEnabled,
      isLoadCloningEnabled,
    },
    tmsIntegrations,
  } = useServiceFeatures();

  const activeTMSIntegration = tmsIntegrations?.find((tms) => tms.id === tmsID);
  const tmsTenant = activeTMSIntegration?.tenant ?? '';
  const isTestingEnvironment =
    activeTMSIntegration?.isTestingEnvironment ?? false;

  const { suggestions, isLoading } = useFetchSuggestions(
    email?.threadID,
    load.ID
  );

  const [tab, setTab] = useState(AvailableTabs.LoadInformation);

  // Derived from the thread's fetch, not copied into state: after a thread switch (or a
  // failed fetch) the previous thread's suggestions are never rendered.
  const modifiedSuggestions = useMemo<SuggestedLoadChange[]>(
    () =>
      (suggestions ?? []).filter(isKitSuggestionActionable).map((sug) => {
        if (sug.pipeline === SuggestionPipelines.CarrierInfo) {
          return {
            ...sug,
            suggested: {
              ...sug.suggested,
              dispatchedTime: sug.suggested.dispatchedTime
                ? sug.suggested.dispatchedTime
                : dayjs().second(0).millisecond(0).toDate(),
              dispatchSource:
                sug.suggested.dispatchSource ?? CheckCallSource.Dispatcher,
            },
          };
        }
        return sug;
      }),
    [suggestions]
  );

  const {
    currentState: { drumkitPlatform },
    setCurrentState,
  } = useContext(SidebarStateContext);

  useEffect(() => {
    const hasThreadLoadRelatedSuggestions = modifiedSuggestions.some((s) =>
      LOAD_VIEW_PIPELINES.includes(s.pipeline)
    );
    setCurrentState((prev) => ({
      ...prev,
      hasThreadLoadRelatedSuggestions,
    }));
  }, [modifiedSuggestions, setCurrentState]);

  useEffect(() => {
    if (!isEmailPlatform(drumkitPlatform) && tmsName) {
      setTab(
        newTMSHost(tmsName as TMS)?.determineDefaultLoadTab() ??
          AvailableTabs.LoadInformation
      );
    }
  }, [drumkitPlatform, tmsName]);

  const normalizedLoad = normalizeLoad(tmsName, load);
  const emailID = email?.id;
  const isLoadCloneAvailable = Boolean(
    isLoadCloningEnabled && emailID && load.ID
  );
  const loadCloneJob = useLoadCloneJob({
    enabled: isLoadCloneAvailable,
    sourceLoadId: load.ID ?? undefined,
    emailId: emailID ?? undefined,
  });

  // Reformat load attributes for context provider
  const fieldAttrs = useMemo(() => {
    const customerFieldAttrs = loadAttributes.customer
      ? Object.entries(loadAttributes.customer).map(([key, value]) => ({
          [`customer.${key}`]: value,
        }))
      : [];
    const billToFieldAttrs = loadAttributes.billTo
      ? Object.entries(loadAttributes.billTo).map(([key, value]) => ({
          [`billTo.${key}`]: value,
        }))
      : [];
    const specificationsFieldAttrs = loadAttributes.specifications
      ? Object.entries(loadAttributes.specifications).map(([key, value]) => ({
          [`specifications.${key}`]: value,
        }))
      : [];
    const pickupFieldAttrs = loadAttributes.pickup
      ? Object.entries(loadAttributes.pickup).map(([key, value]) => ({
          [`pickup.${key}`]: value,
        }))
      : [];
    const consigneeFieldAttrs = loadAttributes.consignee
      ? Object.entries(loadAttributes.consignee).map(([key, value]) => ({
          [`consignee.${key}`]: value,
        }))
      : [];
    const stopsFieldAttrs = loadAttributes.stops
      ? Object.entries(loadAttributes.stops).flatMap(([key, value]) => {
          if (key === 'address') {
            return Object.entries(value).map(([addressKey, addressValue]) => ({
              [`stops.address.${addressKey}`]: addressValue,
            }));
          }
          return [{ [`stops.${key}`]: value }];
        })
      : [];
    const carrierFieldAttrs = loadAttributes.carrier
      ? Object.entries(loadAttributes.carrier).map(([key, value]) => ({
          [`carrier.${key}`]: value,
        }))
      : [];
    const rateDataFieldAttrs = loadAttributes.rateData
      ? Object.entries(loadAttributes.rateData).map(([key, value]) => ({
          [`rateData.${key}`]: value,
        }))
      : [];

    return [
      { poNums: loadAttributes.poNums },
      { mode: loadAttributes.mode },
      ...customerFieldAttrs,
      ...billToFieldAttrs,
      ...specificationsFieldAttrs,
      ...pickupFieldAttrs,
      ...consigneeFieldAttrs,
      ...stopsFieldAttrs,
      ...carrierFieldAttrs,
      ...rateDataFieldAttrs,
    ];
  }, [loadAttributes]);

  const {
    currentState: { clickedSuggestion },
  } = useContext(SidebarStateContext);

  // Redirecting to correct tab after Email AI card is clicked
  useEffect(() => {
    if (!clickedSuggestion) return;

    switch (clickedSuggestion.pipeline) {
      case SuggestionPipelines.CarrierInfo:
      case SuggestionPipelines.ApptConfirmation:
        setTab(AvailableTabs.LoadInformation);
        break;
      case SuggestionPipelines.CheckCall:
        setTab(AvailableTabs.TrackAndTrace);
        break;
      default:
        captureException(
          new Error('Invalid suggestion pipeline: ' + clickedSuggestion)
        );
    }
  }, [clickedSuggestion]);

  const checkCallSuggestions = useMemo(() => {
    if (!suggestions) return [];

    return suggestions.filter(
      (s): s is CheckCallSuggestion =>
        s.status === 'pending' && s.pipeline == SuggestionPipelines.CheckCall
    );
  }, [suggestions]);

  const tabRefs: Record<
    AvailableTabs,
    React.RefObject<HTMLButtonElement | null>
  > = {
    [AvailableTabs.LoadInformation]: useRef<HTMLButtonElement>(null),
    [AvailableTabs.Orders]: useRef<HTMLButtonElement>(null),
    [AvailableTabs.AppointmentScheduling]: useRef<HTMLButtonElement>(null),
    [AvailableTabs.TrackAndTrace]: useRef<HTMLButtonElement>(null),
    [AvailableTabs.Outbox]: useRef<HTMLButtonElement>(null),
  };

  return (
    <>
      {isLoading && <SidebarLoader />}

      {!isLoading && load && (
        <TMSProvider
          tmsName={tmsName}
          tmsID={tmsID}
          tenant={tmsTenant}
          isTestingEnvironment={isTestingEnvironment}
        >
          <KitEmailSuggestions
            suggestions={modifiedSuggestions ?? []}
            threadFreightTrackingIDs={email?.freightTrackingIDs}
            loadEmptyStateType={KitEmptyStateType.NoSuggestionsForLoad}
            tmsName={tmsName as TMS}
            appointmentTimezones={{
              pickup: load.pickup?.timezone,
              dropoff: load.consignee?.timezone,
            }}
          />

          {!load.isPlaceholder && (
            <OverviewSection
              tmsName={tmsName as TMS}
              load={load}
              fromEmail={email?.from ?? null}
              loadCloneRecap={
                load.ID ? (
                  <CloneRecap
                    key={load.ID}
                    loadId={load.ID}
                    onOpenLoad={(identifier) => addTab(identifier, 'load')}
                  />
                ) : undefined
              }
              loadCloneControl={
                isLoadCloneAvailable ? (
                  <LoadCloneTaskCard
                    key={`${load.ID}:${emailID}`}
                    {...loadCloneJob}
                    sourceLoadId={load.ID ?? 0}
                    emailId={emailID ?? 0}
                    onOpenLoad={(loadIdentifier) =>
                      addTab(loadIdentifier, 'load')
                    }
                  />
                ) : undefined
              }
            />
          )}

          {!load.isPlaceholder &&
            tmsName === TMS.ThreeG &&
            !!load.ID &&
            !!email && (
              <div className='px-5'>
                <SaveAttachmentsToThreeG
                  key={`${load.ID}-${email.externalID ?? ''}`}
                  loadId={load.ID}
                  email={email}
                />
              </div>
            )}

          <LoadContextProvider
            fieldAttributes={fieldAttrs}
            loadAttrsObj={loadAttributes}
            invalidateLoad={invalidate}
          >
            <Tabs
              value={tab}
              onValueChange={(tab) => setTab(tab as AvailableTabs)}
              className='w-full h-full flex-1 shrink-0 flex flex-col'
            >
              <TabsList className='w-full overflow-x-auto flex gap-0'>
                <TabsTrigger
                  value={AvailableTabs.LoadInformation}
                  ref={tabRefs.loadInformation}
                  variant={TabsTriggerVariants.LoadTabs}
                  data-testid='load-tab-load-info'
                >
                  <InfoCircleIcon
                    className='min-h-[18px] max-h-[18px] min-w-[18px] max-w-[18px]'
                    strokeWidth={1}
                  />
                  <Typography className='overflow-x-hidden' textColor='brand'>
                    Load Info
                  </Typography>
                </TabsTrigger>
                {isOrderEnabled &&
                  (tmsName === TMS.FreightFlow || tmsName === TMS.ThreeG) && (
                    <TabsTrigger
                      value={AvailableTabs.Orders}
                      ref={tabRefs.orders}
                      variant={TabsTriggerVariants.LoadTabs}
                      data-testid='load-tab-orders'
                    >
                      <PackageIcon
                        className='min-h-[18px] max-h-[18px] min-w-[18px] max-w-[18px]'
                        strokeWidth={1}
                      />
                      <Typography
                        className='overflow-x-hidden'
                        textColor='brand'
                      >
                        Orders
                      </Typography>
                    </TabsTrigger>
                  )}
                {/* Temporarily disable appointment scheduling for 3G */}
                {isAppointmentSchedulingEnabled && tmsName !== TMS.ThreeG && (
                  <TabsTrigger
                    value={AvailableTabs.AppointmentScheduling}
                    ref={tabRefs.appointmentScheduling}
                    variant={TabsTriggerVariants.LoadTabs}
                    data-testid='load-tab-appointments'
                  >
                    <CalendarIcon
                      className='min-h-[18px] max-h-[18px] min-w-[18px] max-w-[18px]'
                      strokeWidth={1}
                    />
                    <Typography className='overflow-x-hidden' textColor='brand'>
                      Appointments
                    </Typography>
                  </TabsTrigger>
                )}
                {/* Temporarily disable track and trace for 3G */}
                {isTrackAndTraceEnabled && tmsName !== TMS.ThreeG && (
                  <TabsTrigger
                    value={AvailableTabs.TrackAndTrace}
                    ref={tabRefs.trackAndTrace}
                    variant={TabsTriggerVariants.LoadTabs}
                    data-testid='load-tab-track-trace'
                  >
                    <RouteIcon
                      className='min-h-[18px] max-h-[18px] min-w-[18px] max-w-[18px]'
                      strokeWidth={1}
                    />
                    <Typography className='overflow-x-hidden' textColor='brand'>
                      Track & Trace
                    </Typography>
                  </TabsTrigger>
                )}

                {/* Temporarily disable outbox for 3G */}
                {isCarrierEmailOutboxEnabled && tmsName !== TMS.ThreeG && (
                  <TabsTrigger
                    value={AvailableTabs.Outbox}
                    ref={tabRefs.outbox}
                    variant={TabsTriggerVariants.LoadTabs}
                    data-testid='load-tab-outbox'
                  >
                    <MailsIcon
                      className='min-h-[18px] max-h-[18px] min-w-[18px] max-w-[18px]'
                      strokeWidth={1}
                    />
                    <Typography className='overflow-x-hidden' textColor='brand'>
                      Outbox
                    </Typography>
                  </TabsTrigger>
                )}
              </TabsList>
              <div className='flex-1 shrink-0'>
                <TabsContent
                  value={AvailableTabs.LoadInformation}
                  className='px-5'
                >
                  <ErrorBoundary>
                    {(() => {
                      const formKey = `${load.ID}-${invalidateKey}`;
                      switch (tmsName) {
                        case TMS.Turvo:
                          return (
                            <TurvoLoadForm
                              isCreateMode={false}
                              load={load}
                              externalLinks={externalLinks}
                              key={formKey}
                            />
                          );
                        case TMS.Revenova:
                          return (
                            <UnifiedRevenovaLoadForm
                              isCreateMode={false}
                              load={load}
                              externalLinks={externalLinks || undefined}
                              key={formKey}
                            />
                          );
                        case TMS.ThreeG:
                          return (
                            <UnifiedThreeGLoadForm
                              isCreateMode={false}
                              load={load}
                              externalLinks={externalLinks}
                              key={formKey}
                            />
                          );
                        case TMS.GlobalTranzTMS:
                          return (
                            <UnifiedGlobalTranzForm
                              isCreateMode={false}
                              load={load}
                              externalLinks={externalLinks}
                              key={formKey}
                            />
                          );
                        case TMS.TransportPro:
                          return (
                            <UnifiedTransportProLoadForm
                              isCreateMode={false}
                              load={load}
                              externalLinks={externalLinks}
                              key={formKey}
                            />
                          );
                        case TMS.PLS:
                          return (
                            <UnifiedPlsLoadForm
                              isCreateMode={false}
                              load={load}
                              key={formKey}
                            />
                          );
                        default:
                          return (
                            <LoadInformationTab
                              load={load}
                              externalLinks={externalLinks}
                              key={formKey}
                            />
                          );
                      }
                    })()}
                  </ErrorBoundary>
                </TabsContent>
                {isOrderEnabled &&
                  (tmsName === TMS.FreightFlow || tmsName === TMS.ThreeG) && (
                    <TabsContent value={AvailableTabs.Orders}>
                      <ErrorBoundary>
                        <OrdersTab load={load} />
                      </ErrorBoundary>
                    </TabsContent>
                  )}
                {isAppointmentSchedulingEnabled && (
                  <TabsContent value={AvailableTabs.AppointmentScheduling}>
                    <ErrorBoundary>
                      <AppointmentSchedulingSection
                        normalizedLoad={normalizedLoad}
                        key={load.freightTrackingID}
                        loadPickupWarehouse={
                          loadPickupWarehouse?.warehouseID
                            ? loadPickupWarehouse
                            : null
                        }
                        loadDropoffWarehouse={
                          loadDropoffWarehouse?.warehouseID
                            ? loadDropoffWarehouse
                            : null
                        }
                      />
                    </ErrorBoundary>
                  </TabsContent>
                )}
                {isTrackAndTraceEnabled && (
                  <TabsContent
                    value={AvailableTabs.TrackAndTrace}
                    className='px-4'
                  >
                    {isCheckCallCarrierSOPEnabled && (
                      <ErrorBoundary>
                        <CarrierCheckInSection
                          normalizedLoad={normalizedLoad}
                          key={load.freightTrackingID}
                          setTab={setTab}
                        />
                      </ErrorBoundary>
                    )}
                    <ErrorBoundary>
                      <CheckCallSection
                        normalizedLoad={normalizedLoad}
                        key={normalizedLoad.freightTrackingID}
                        suggestions={checkCallSuggestions}
                      />
                    </ErrorBoundary>
                    {isExceptionsEnabled && (
                      <ErrorBoundary>
                        <ExceptionSection
                          normalizedLoad={normalizedLoad}
                          key={normalizedLoad.freightTrackingID}
                        />
                      </ErrorBoundary>
                    )}
                    {isCheckCallShipperSOPEnabled && (
                      <ErrorBoundary>
                        <ShipperCheckInSection
                          normalizedLoad={normalizedLoad}
                          key={normalizedLoad.freightTrackingID}
                        />
                      </ErrorBoundary>
                    )}
                    {isCheckCallNotesEnabled && (
                      <ErrorBoundary>
                        <NoteSection
                          normalizedLoad={normalizedLoad}
                          key={normalizedLoad.freightTrackingID}
                        />
                      </ErrorBoundary>
                    )}
                  </TabsContent>
                )}

                {isCarrierEmailOutboxEnabled &&
                  pendingOutboxEmails?.carrierEmails && (
                    <TabsContent value={AvailableTabs.Outbox}>
                      <ErrorBoundary>
                        <OutboxTab
                          normalizedLoad={normalizedLoad}
                          carrierEmails={pendingOutboxEmails?.carrierEmails}
                          setTab={setTab}
                        />
                      </ErrorBoundary>
                    </TabsContent>
                  )}
              </div>
            </Tabs>
          </LoadContextProvider>
        </TMSProvider>
      )}
    </>
  );
}
